import React, { useState } from "react";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/webvideo/UserPublicDataProvider";
import { isMobile } from "@scrile/tools/dist/lib/browserUtils";
import { MediaConstraints, MediaDevicePermissions, ProduceStream, ProduceUser, UserListItemIcon } from "types";
import BaseAvatar from "components/BaseAvatar";
import BaseButton from "components/BaseButton";
import BaseIcon from "components/BaseIcon";
import ModalExitConfirmation from "./components/ModalExitConfirmation";
import PanelControlButtons from "./components/PanelControlButtons";
import PlayStreamButton from "./components/PlayStreamButton";
import RightPanel, { RightPanelView } from "./components/RightPanel";
import StreamsLayout from "./components/StreamsLayout";
import TransitionControl from "./components/TransitionControl";
import useController from "./controller";
import useViewController from "./viewController";
import "./style.scss";

interface Props {
  className?: string;
  produce: boolean;
  produceUser: UserPublicData | null;
  threadId: string | undefined;
  loading: boolean;
  userList: LiveChatUser[];
  onClose: () => void;
  token: string;
  showConfirmationExit: boolean;
  audioInputs?: MediaDeviceInfo[];
  videoInputs?: MediaDeviceInfo[];
  managedConstraints: MediaConstraints | null;
  onChangeConstraints?: (constraints: MediaConstraints) => void;
  permissions?: MediaDevicePermissions;
  disableAttachments?: boolean;
  me: ProduceUser | null;
  streams: ProduceStream[];
  slotContentConfirmationExit?: JSX.Element;
  disableUserList?: boolean;
  slotTimer?: JSX.Element;
  slotVideoScreen?: JSX.Element;
  slotHeaderButtons?: JSX.Element;
  slotControlButtons?: JSX.Element;
  forceHideControlButtons?: boolean;
  userListItemIcon?: UserListItemIcon;
  enableUserSearch?: boolean;
  chatInLive?: boolean;
  includeGuestsList?: boolean;
  allowGuestMessage?: boolean;
  hideVolumeControl?: boolean;
  hideChatOnStart?: boolean;
}

function StreamView({
  produceUser,
  produce,
  loading,
  threadId,
  userList,
  onClose,
  token,
  managedConstraints,
  audioInputs,
  videoInputs,
  onChangeConstraints,
  permissions,
  disableAttachments,
  disableUserList,
  showConfirmationExit,
  forceHideControlButtons,
  slotTimer,
  slotVideoScreen,
  slotHeaderButtons,
  slotContentConfirmationExit,
  slotControlButtons,
  userListItemIcon,
  enableUserSearch,
  chatInLive,
  allowGuestMessage,
  className,
  includeGuestsList,
  me,
  streams,
  hideVolumeControl,
  hideChatOnStart,
}: Props) {
  const {
    changeControls,
    changeRightPanel,
    onClickEndCall,
    desktopUp,
    mobileLandscape,
    showControls,
    showControlsButtons,
    showStreamPlayButton,
    onShowControls,
    onCloseEvent,
    displayControls,
    usersCount,
    onOpenChat,
    setHasUnreadMessage,
    toggleFullscreen,
  } = useViewController({
    userList,
    onClose,
    showConfirmationExit,
    hideChatOnStart,
    chatInLive,
  });

  const { onSwitchCam, onToggleCam, onToggleMic, onToggleScreenSharing, onToggleBlur } = useController(
    managedConstraints,
    onChangeConstraints
  );

  const [managedPlayer, setManagedPlayer] = useState<HTMLVideoElement | null>(null);
  const [selectedStreamId, setSelectedStreamId] = useState<string | null>(null);

  const mobileChatMode = displayControls.rightPanel === RightPanelView.CHAT && !desktopUp;
  const mobileChatInLive = chatInLive && !desktopUp;
  const settingsMode = displayControls.rightPanel === RightPanelView.SETTINGS;
  const usersListMode = displayControls.rightPanel === RightPanelView.USERS;
  const rightPanelHidden = displayControls.rightPanelHidden;
  const rightControlIndent = rightPanelHidden && !!selectedStreamId && streams.length > 2;

  const cls = ["stream-view"];
  if (mobileChatMode) cls.push("__chat-mode");
  if (usersListMode) cls.push("__users-mode");
  if (mobileLandscape) cls.push("__landscape-mode");
  if (!mobileChatInLive) cls.push("__modal-chat");
  if (rightPanelHidden) cls.push("__right-panel-hidden");
  if (displayControls.fullscreen) cls.push("__fullscreen");
  if (className) cls.push(className);

  return (
    <div className={cls.join(" ")} onClick={onShowControls}>
      <div className="stream-view__left-side">
        <div className="stream-view__header">
          <TransitionControl className="stream-view__header-inner" showControl={showControls || usersListMode}>
            {slotTimer}
            <div className="stream-view__header-buttons">
              {!disableUserList && (
                <>
                  <div className="stream-view__header-bubble mr-2">
                    <span>{usersCount}</span>
                  </div>
                  <BaseButton
                    className={
                      "stream-view__header-btn" +
                      (produce ? "" : " __disabled") +
                      (usersListMode && !rightPanelHidden ? " __active" : "")
                    }
                    name={"users"}
                    disabled={!produce}
                    onClick={() => changeRightPanel(RightPanelView.USERS)}
                  >
                    <BaseIcon className="stream-view__header-icon">consultants</BaseIcon>
                  </BaseButton>
                </>
              )}
              {slotHeaderButtons && slotHeaderButtons}
              {managedConstraints && desktopUp && !isMobile() && (
                <BaseButton
                  className={"stream-view__header-btn" + (settingsMode && !rightPanelHidden ? " __active" : "")}
                  name={"settings"}
                  onClick={() => changeRightPanel(RightPanelView.SETTINGS)}
                >
                  <BaseIcon className="stream-view__header-icon">settings</BaseIcon>
                </BaseButton>
              )}
              {desktopUp && (
                <BaseButton className="stream-view__header-btn" name={"fullscreen"} onClick={toggleFullscreen}>
                  <BaseIcon className="stream-view__header-icon">
                    {displayControls.fullscreen ? "minimize" : "maximize"}
                  </BaseIcon>
                </BaseButton>
              )}
            </div>
          </TransitionControl>
        </div>

        <div className={"stream-view__video-wrapper"}>
          <TransitionControl
            className="stream-view__video-wrapper-header"
            showControl={desktopUp || (!showControls && !usersListMode)}
          >
            {produceUser && (
              <div className="stream-view__live-user">
                <BaseAvatar className="stream-view__live-user-avatar" size={30} user={produceUser} />
                <p className="stream-view__live-user-name label fw-semibold">{produceUser.screenName}</p>
              </div>
            )}
          </TransitionControl>
          {slotVideoScreen ? (
            slotVideoScreen
          ) : (
            <StreamsLayout
              streams={streams}
              me={me}
              setManagedPlayer={setManagedPlayer}
              selectedStreamId={selectedStreamId}
              setSelectedStreamId={setSelectedStreamId}
              showControl={desktopUp || (!showControls && !usersListMode)}
              large={rightPanelHidden}
            />
          )}
          {showStreamPlayButton && <PlayStreamButton />}
          <div className={["stream-view__shadow", rightControlIndent ? "__right-indent" : ""].join(" ")} />
          <PanelControlButtons
            className={["stream-view__control-buttons", rightControlIndent ? "__right-indent" : ""].join(" ")}
            loading={loading}
            showControlsButtons={!forceHideControlButtons && showControlsButtons}
            constraints={managedConstraints}
            onClickEndCall={onClickEndCall}
            onSwitchCam={onSwitchCam}
            onToggleMic={onToggleMic}
            onToggleCam={onToggleCam}
            onToggleBlur={onToggleBlur}
            onToggleChat={onOpenChat}
            onToggleScreenSharing={onToggleScreenSharing}
            onToggleGrid={() => setSelectedStreamId(null)}
            showChatButton={rightPanelHidden || displayControls.rightPanel !== RightPanelView.CHAT}
            player={hideVolumeControl ? null : managedPlayer}
            slotControlButtons={slotControlButtons}
            muted={me?.user?.muted}
            showGridBtn={!!selectedStreamId && streams.length > 2}
            hasUnreadMessage={displayControls.hasUnreadMessage}
            hintPosition={desktopUp ? "top" : "left"}
            permissions={permissions}
          />
        </div>

        {displayControls.confirmationExit && (
          <ModalExitConfirmation
            slotContentConfirmationExit={slotContentConfirmationExit}
            onConfirm={onCloseEvent}
            onCancel={() => changeControls("confirmationExit", false)}
          />
        )}
      </div>
      <div
        className={
          "stream-view__right-side" +
          (mobileChatMode ? " __chat-mode" : "") +
          (usersListMode ? " __users-mode" : "") +
          (mobileLandscape ? " __landscape-mode" : "") +
          ((mobileChatMode && !mobileChatInLive) || usersListMode ? " __modal-mode" : "")
        }
      >
        <RightPanel
          loading={loading}
          disableAttachments={disableAttachments}
          token={token}
          threadId={threadId}
          devices={audioInputs && videoInputs && { audioInputs, videoInputs }}
          onChangeConstraints={onChangeConstraints}
          constraints={managedConstraints}
          permissions={permissions}
          userList={userList}
          modalChatView={!chatInLive}
          enableUserSearch={enableUserSearch}
          allowGuestMessage={allowGuestMessage}
          me={me?.user}
          userListItemIcon={userListItemIcon}
          showSection={displayControls.rightPanel}
          includeGuestsList={includeGuestsList}
          changeControls={changeRightPanel}
          setHasUnreadMessage={setHasUnreadMessage}
        />
      </div>
    </div>
  );
}

export default StreamView;
